import { AlertTriangle, ArrowRight, CheckCircle2, Info } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';

export type AttentionSeverity = 'critical' | 'warning';

export interface AttentionItem {
  id: string;
  severity: AttentionSeverity;
  /** What is wrong, in a sentence somebody can act on without opening anything. */
  message: string;
  /** Where to go and fix it. */
  to: string;
  action: string;
}

/**
 * The things that are wrong, and nothing else.
 *
 * ## Why this is the top of the dashboard and not a row of stat tiles
 *
 * Everything this platform learned building monitoring and backups points the same way. A wall of
 * green numbers trains somebody to stop reading it, and then the one day a number turns red they
 * have already stopped looking. The useful question a dashboard answers is not "how many servers do
 * I have" — the customer knows that — it is "is anything wrong right now".
 *
 * So this renders **only when something is wrong**. When nothing is, it says so in one line and
 * gets out of the way. That is deliberately the least visually interesting state, because a quiet
 * dashboard should feel like nothing to look at.
 *
 * ## Loading is not "nothing is wrong"
 *
 * The caller passes `ready` explicitly. A panel that showed the all-clear while its queries were
 * still in flight would flash reassurance and then contradict itself — and somebody who glanced at
 * the right moment would walk away wrongly reassured.
 *
 * ## "Nothing is wrong" is not the same as "nothing is being watched"
 *
 * A customer with no monitors and no backups has no *findings*, which is not the same as being
 * covered. Telling them "everything being monitored is responding" is vacuously true and reads as
 * reassurance they have not earned — the exact shape of dishonesty the monitoring module's
 * staleness flag exists to prevent, arriving through a different door.
 *
 * So `watching` is separate from `items`. Nothing found *and* nothing watched is its own state,
 * and it says what is missing rather than congratulating them.
 *
 * ## The all-clear names only what exists
 *
 * It used to be one fixed sentence — "everything being monitored is responding, backups are
 * running, and no run is waiting for you" — shown to anybody with a single monitor. A customer with
 * two monitors and no backups at all was told their backups were running, and asked why. Monitoring
 * and backups are now reported separately, each claim is made only when there is something behind
 * it, and a missing half is said plainly instead.
 */
export function AttentionPanel({
  items,
  ready,
  watching,
}: {
  items: AttentionItem[];
  ready: boolean;
  /** What is actually being watched. See the component docblock. */
  watching: { monitors: boolean; backups: boolean };
}) {
  if (!ready) {
    return (
      <Card>
        <CardContent className="py-4">
          <p className="text-sm text-muted-foreground">Checking your infrastructure…</p>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0 && !watching.monitors && !watching.backups) {
    return (
      <Card>
        <CardContent className="flex items-start gap-3 py-4">
          <Info className="mt-0.5 size-5 shrink-0 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            Nothing is being watched yet, so there is nothing to report. Add a{' '}
            <Link to="/monitoring" className="text-primary hover:underline">
              monitor
            </Link>{' '}
            to find out when a site stops responding, and a{' '}
            <Link to="/backups" className="text-primary hover:underline">
              backup schedule
            </Link>{' '}
            so there is a copy to go back to.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (items.length === 0) {
    const fine = [
      watching.monitors ? 'everything being monitored is responding' : null,
      watching.backups ? 'backups are running on schedule' : null,
      'no run is waiting for you',
    ].filter((part): part is string => part !== null);
    const said = fine.length === 1 ? fine[0] : `${fine.slice(0, -1).join(', ')} and ${fine[fine.length - 1]}`;

    return (
      <Card className="border-emerald-600/30 bg-emerald-500/5">
        <CardContent className="flex items-center gap-3 py-4">
          <CheckCircle2 className="size-5 shrink-0 text-emerald-500" />
          <p className="text-sm">
            Nothing needs your attention. {said.charAt(0).toUpperCase() + said.slice(1)}.
            {!watching.backups && (
              <>
                {' '}
                No backups are set up yet:{' '}
                <Link to="/backups" className="text-primary hover:underline">
                  add a backup schedule
                </Link>{' '}
                so there is a copy to go back to.
              </>
            )}
            {!watching.monitors && (
              <>
                {' '}
                Nothing is monitored yet:{' '}
                <Link to="/monitoring" className="text-primary hover:underline">
                  add a monitor
                </Link>{' '}
                to find out when a site stops responding.
              </>
            )}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Critical first. Within a severity the caller's order is preserved, which is the order the
  // checks run in — stable, so items do not jump around between refreshes.
  const ordered = [...items].sort((a, b) =>
    a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1,
  );

  return (
    <Card className="border-amber-600/30">
      <CardContent className="space-y-2 py-4">
        <div className="flex items-center gap-2">
          <AlertTriangle className="size-4 text-amber-500" />
          <h2 className="text-sm font-semibold">
            {ordered.length === 1 ? '1 thing needs attention' : `${ordered.length} things need attention`}
          </h2>
        </div>

        <ul className="divide-y">
          {ordered.map((item) => (
            <li key={item.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span className="flex items-start gap-2 text-sm">
                <span
                  aria-hidden
                  className={`mt-1.5 size-2 shrink-0 rounded-full ${
                    item.severity === 'critical' ? 'bg-danger' : 'bg-amber-500'
                  }`}
                />
                {item.message}
              </span>
              <Link
                to={item.to}
                className="inline-flex shrink-0 items-center gap-1 text-sm text-primary hover:underline"
              >
                {item.action}
                <ArrowRight className="size-3" />
              </Link>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
}
